import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import {
    TEXT_SIGNATURE_MAX, companySettingsFromRow, isGsm7, loadCompanySignatureSettings, loadEmailCompanyBlock,
    resolveActiveSignatureCompany, saveActiveSignatureCompany, saveCompanySignatureSettings, signatureErrorMessage,
} from '../../../lib/signatures';
import { useSaveToast } from '../../ui/SaveToast';
import AppLink from '../../ui/AppLink';
import HelpButton from '../../ui/HelpButton';
import { EmailCompanyBlock, OptionButtons, TextSignatureProblem } from './SignatureParts';
import { D2_LABEL, D2_TEXTAREA } from './d2Fields';

// BL-177 Step 2d (section 10): Admin Center › Communications › Signatures. Company admins and platform admins only
// (the route is admin-gated too; this view also refuses to show controls to anyone else).

const COMPANY_OPTIONS = [{ value: true, label: 'Always on' }, { value: false, label: 'Off' }];
const PERSONAL_OPTIONS = [{ value: 'off', label: 'Off' }, { value: 'optional', label: 'Optional' }, { value: 'required', label: 'Required' }];
const FAILED_PREFIX = "Couldn't save. Switched back.";

export default function CompanySignaturesView({
    orgId,
    userId = '',
    isAdmin = false,
    companyName = '',
    brandEntityId = '',
    headerBrandEntityId = '',
    platformDoorContext = null,
    client = supabaseClient,
}) {
    const toast = useSaveToast();
    const [settings, setSettings] = useState(null);
    const [draftText, setDraftText] = useState('');
    const [block, setBlock] = useState(null);
    const [brandOptions, setBrandOptions] = useState([]);
    const [showCompanyPicker, setShowCompanyPicker] = useState(false);
    const [selectedBrandEntityId, setSelectedBrandEntityId] = useState('');
    const [error, setError] = useState('');
    const saved = useRef(null);
    const selectedBrand = brandOptions.find((option) => option.id === selectedBrandEntityId);
    const company = companyName || selectedBrand?.label || block?.name || 'your company';

    const load = useCallback(async () => {
        try {
            const [row, companyContext] = await Promise.all([
                loadCompanySignatureSettings(client, orgId),
                resolveActiveSignatureCompany(client, {
                    orgId,
                    userId,
                    doorEntityId: platformDoorContext?.entity_id || '',
                    headerEntityId: headerBrandEntityId || brandEntityId,
                }),
            ]);
            const next = companySettingsFromRow(row);
            saved.current = next;
            setSettings(next);
            setDraftText(next.text_signature);
            setBrandOptions(companyContext.options);
            setShowCompanyPicker(companyContext.showPicker);
            setSelectedBrandEntityId(companyContext.selectedId);
            setError('');
        } catch (loadError) {
            setError(loadError.message || 'Could not load the signature settings.');
        }
    }, [brandEntityId, client, headerBrandEntityId, orgId, platformDoorContext?.entity_id, userId]);
    useEffect(() => { if (isAdmin && orgId) load(); }, [isAdmin, orgId, load]);
    useEffect(() => {
        let current = true;
        if (!selectedBrandEntityId) {
            setBlock(null);
            return () => { current = false; };
        }
        loadEmailCompanyBlock(client, orgId, selectedBrandEntityId)
            .then((nextBlock) => { if (current) setBlock(nextBlock); })
            .catch((loadError) => { if (current) setError(loadError.message || 'Could not load the company email block.'); });
        return () => { current = false; };
    }, [client, orgId, selectedBrandEntityId]);

    async function save(patch) {
        const before = saved.current;
        const next = { ...before, ...patch };
        setSettings(next);
        if (patch.text_signature !== undefined) setDraftText(patch.text_signature);
        try {
            await saveCompanySignatureSettings(client, orgId, next);
            saved.current = next;
            toast.saved();
        } catch (saveError) {
            setSettings(before);
            setDraftText(before.text_signature);
            toast.failed(`${FAILED_PREFIX} ${signatureErrorMessage(saveError)}`);
        }
    }

    function commitText() {
        const text = draftText.replace(/\r\n?/g, '\n');
        if (text === saved.current.text_signature) return;
        if (!isGsm7(text)) return; // the plain-English message under the box explains; nothing is saved until it's fixed
        save({ text_signature: text });
    }

    if (!isAdmin) {
        return (
            <div className="ia-d2 rounded-2xl p-5" role="alert">
                Only {company}&apos;s admins and platform admins can change signatures.
            </div>
        );
    }

    return (
        <div className="ia-d2 max-w-full space-y-4 rounded-2xl p-3 sm:p-5" aria-label="Company signatures">
            {showCompanyPicker && (
                <div className="max-w-3xl">
                    <label htmlFor="company-signature-brand" className={D2_LABEL}>Which company?</label>
                    <select id="company-signature-brand" className={`${D2_TEXTAREA} mt-2`}
                        value={selectedBrandEntityId} onChange={(event) => {
                            const nextId = event.target.value;
                            setSelectedBrandEntityId(nextId);
                            try {
                                saveActiveSignatureCompany(undefined, orgId, userId, nextId);
                                setError('');
                            } catch (saveError) {
                                setError(saveError.message || 'Could not remember your company choice in this browser.');
                            }
                        }}>
                        <option value="">Choose a company or brand</option>
                        {brandOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                </div>
            )}
            <p className="text-sm">
                Two parts, always in the same order: the person&apos;s signature, then the company&apos;s. Only {company}&apos;s admins and
                platform admins can change these.
            </p>
            {error && <p role="alert" className="rounded-lg border border-red-500/60 bg-red-900/40 px-3 py-2 text-sm">{error}</p>}
            {settings && (
                <section className="ia-d2-card max-w-3xl space-y-5">
                    <div className="space-y-2">
                        <h2 className={`${D2_LABEL} flex items-center gap-2`}>
                            Company signature
                            <HelpButton
                                title="Company signature"
                                what={`${company}'s name and contact lines at the end of staff messages, after the person's own signature.`}
                                who="Every customer who gets a text, WhatsApp or email written by staff."
                                when="On every eligible staff-written text and email. Never on WhatsApp templates or automatic notifications."
                            />
                        </h2>
                        <OptionButtons label="Company signature" options={COMPANY_OPTIONS} value={settings.always_on}
                            onChange={(value) => save({ always_on: value })} />
                        {!settings.always_on && <p className="text-sm">Off: messages carry only each person&apos;s own signature (if any).</p>}
                    </div>

                    <div>
                        <label htmlFor="company-text-signature" className={D2_LABEL}>
                            Text and WhatsApp company signature <span className="font-normal">· multi-line</span>
                        </label>
                        <textarea id="company-text-signature" rows={4} value={draftText} className={`${D2_TEXTAREA} mt-2`}
                            maxLength={TEXT_SIGNATURE_MAX} placeholder={'Company name\nPhone\nWebsite'}
                            onChange={(event) => setDraftText(event.target.value)} onBlur={commitText} />
                        <TextSignatureProblem text={draftText} onFix={(fixed) => save({ text_signature: fixed })} />
                    </div>

                    <div className="space-y-2">
                        <h2 className={D2_LABEL}>Email company block</h2>
                        <p className="text-sm" data-testid="signature-company-context">
                            Company for this email block: {selectedBrand?.label || 'Choose a company'}
                        </p>
                        <EmailCompanyBlock block={block} />
                        <p className="text-sm">
                            Logo, name, phone and website come from Brands &amp; Locations.{' '}
                            <AppLink to="/admin/organization" className="font-bold text-[#5EEAD4] underline">Edit in Brands &amp; Locations</AppLink>
                        </p>
                    </div>

                    <div className="space-y-2">
                        <h2 className={`${D2_LABEL} flex items-center gap-2`}>
                            Personal lines
                            <HelpButton
                                title="Personal lines"
                                what="Each person's own sign-off (for example “- Val”), placed before the company signature."
                                who="Off: nobody adds one. Optional: people can add one and remove it from a single message. Required: everyone's messages carry it."
                                when="Whenever the company signature is added."
                            />
                        </h2>
                        <OptionButtons label="Personal lines" options={PERSONAL_OPTIONS} value={settings.personal_signature_mode}
                            onChange={(value) => save({ personal_signature_mode: value })} />
                    </div>
                </section>
            )}
        </div>
    );
}
